import { useState, useEffect, useCallback } from 'react';
import { OrderIcon } from '../components/Icons';
import { fetchOrders, confirmDelivery, cancelOrder, initializePayment, verifyPayment, fetchSettings, verifyOrderPayment } from '../db';
import ConfirmModal from '../components/ConfirmModal';
import RatingModal from '../components/RatingModal';
import { toast } from 'react-toastify';

const formatPrice = (amount) => `₦${Number(amount).toLocaleString('en-NG')}`;

// ── Status helpers ────────────────────────────────────────────────────────────

// Map DB status → display info
const STATUS_MAP = {
  placed:         { label: 'Order Placed',       color: 'bg-amber-50 text-amber-700 border-amber-200',    dot: 'bg-amber-400'   },
  unpaid:         { label: 'Awaiting Payment',   color: 'bg-gray-50 text-gray-600 border-gray-200',       dot: 'bg-gray-400'    },
  paid:           { label: 'Payment Confirmed',  color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  processing:     { label: 'Processing',         color: 'bg-blue-50 text-blue-700 border-blue-200',       dot: 'bg-blue-500'    },
  shipped:        { label: 'Shipped',            color: 'bg-violet-50 text-violet-700 border-violet-200', dot: 'bg-violet-500'  },
  delivered:      { label: 'Delivered',          color: 'bg-green-50 text-green-700 border-green-200',    dot: 'bg-green-500'   },
  cancelled:      { label: 'Cancelled',          color: 'bg-red-50 text-red-600 border-red-200',          dot: 'bg-red-500'     },
  failed:         { label: 'Payment Failed',     color: 'bg-red-50 text-red-600 border-red-200',          dot: 'bg-red-400'     },
  refund_pending: { label: 'Refund Pending',     color: 'bg-orange-50 text-orange-600 border-orange-200', dot: 'bg-orange-400'  },
  refunded:       { label: 'Refunded',           color: 'bg-teal-50 text-teal-700 border-teal-200',       dot: 'bg-teal-500'    },
  ready_for_pickup: { label: 'Ready for Pickup', color: 'bg-yellow-50 text-yellow-700 border-yellow-200', dot: 'bg-yellow-500'  },
  picked_up:      { label: 'Picked Up',          color: 'bg-green-50 text-green-700 border-green-200',    dot: 'bg-green-500'   },
};

const getStatusInfo = (status, paymentStatus) => {
  const s = String(status || '').toLowerCase();
  const p = String(paymentStatus || '').toLowerCase();

  // Refund states take absolute priority
  if (p === 'refunded')       return STATUS_MAP.refunded;
  if (p === 'refund_pending') return STATUS_MAP.refund_pending;

  // Terminal states
  if (s === 'cancelled') return STATUS_MAP.cancelled;
  if (s === 'failed' || p === 'failed') return STATUS_MAP.failed;

  // Active fulfilment pipeline
  if (s === 'delivered')  return STATUS_MAP.delivered;
  if (s === 'picked_up')  return STATUS_MAP.picked_up;
  if (s === 'shipped')    return STATUS_MAP.shipped;
  if (s === 'ready_for_pickup') return STATUS_MAP.ready_for_pickup;
  if (s === 'processing') return STATUS_MAP.processing;

  // Payment status
  if (s === 'paid' || p === 'paid') return STATUS_MAP.paid;

  // Pending order not yet paid
  if (p === 'unpaid' || s === 'pending') return STATUS_MAP.unpaid;

  return STATUS_MAP[s] || { label: s, color: 'bg-gray-50 text-gray-600 border-gray-200', dot: 'bg-gray-400' };
};

// ── Tracking steps config ─────────────────────────────────────────────────────
// Standard e-commerce pipeline: Order Placed → Payment Confirmed → Processing → Shipped → Delivered
// Each step has the DB statuses that activate it.

const DELIVERY_STEPS = [
  {
    key: 'placed',
    label: 'Order Placed',
    desc: 'Your order has been received.',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>,
    activeOn: ['pending', 'unpaid', 'paid', 'processing', 'shipped', 'delivered'],
  },
  {
    key: 'paid',
    label: 'Payment Confirmed',
    desc: 'Your payment has been verified.',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>,
    activeOn: ['paid', 'processing', 'shipped', 'delivered'],
  },
  {
    key: 'processing',
    label: 'Processing',
    desc: 'Your order is being prepared.',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    activeOn: ['processing', 'shipped', 'delivered'],
  },
  {
    key: 'shipped',
    label: 'Shipped',
    desc: 'Your order is on its way.',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v6a2 2 0 01-2 2h-3m-1 4a2 2 0 11-4 0 2 2 0 014 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 18a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
    activeOn: ['shipped', 'delivered'],
  },
  {
    key: 'delivered',
    label: 'Delivered',
    desc: 'Your order has arrived!',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    activeOn: ['delivered'],
  },
];

const PICKUP_STEPS = [
  DELIVERY_STEPS[0],
  { ...DELIVERY_STEPS[1], activeOn: ['paid', 'processing', 'ready_for_pickup', 'picked_up'] },
  { ...DELIVERY_STEPS[2], activeOn: ['processing', 'ready_for_pickup', 'picked_up'] },
  {
    key: 'ready_for_pickup',
    label: 'Ready for Pickup',
    desc: 'Your order is ready at the store.',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    activeOn: ['ready_for_pickup', 'picked_up'],
  },
  {
    key: 'picked_up',
    label: 'Picked Up',
    desc: 'You have collected your order!',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    activeOn: ['picked_up'],
  },
];

// Resolve which step is "current" (the last completed one)
const getTrackingConfig = (order) => {
  const isPickup = (order.shippingAddress || '').toLowerCase().includes('store pickup');
  const steps = isPickup ? PICKUP_STEPS : DELIVERY_STEPS;
  
  const s = String(order.status || '').toLowerCase();
  const p = String(order.paymentStatus || '').toLowerCase();

  let currentStepIdx = 0;
  if (isPickup) {
    if (s === 'picked_up') currentStepIdx = 4;
    else if (s === 'ready_for_pickup') currentStepIdx = 3;
    else if (s === 'processing') currentStepIdx = 2;
    else if (s === 'paid' || p === 'paid') currentStepIdx = 1;
  } else {
    if (s === 'delivered') currentStepIdx = 4;
    else if (s === 'shipped') currentStepIdx = 3;
    else if (s === 'processing') currentStepIdx = 2;
    else if (s === 'paid' || p === 'paid') currentStepIdx = 1;
  }
  
  return { steps, currentStepIdx };
};

const getStepHistory = (history, stepKey) => {
  if (!history || !Array.isArray(history)) return null;
  return history.find(h => {
    if (stepKey === 'placed') return h.status === 'pending' || h.status === 'placed';
    if (stepKey === 'paid') return h.status === 'paid' || h.status === 'processing';
    return h.status === stepKey;
  });
};

export default function OrdersPage({ onNavigate }) {
  const [orders, setOrders]               = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isConfirming, setIsConfirming]   = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [loading, setLoading]             = useState(true);
  const [paystackKey, setPaystackKey]     = useState('');
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);

  useEffect(() => {
    fetchSettings().then(s => setPaystackKey(s?.paystack_public_key || '')).catch(() => {});
  }, []);

  const refreshOrders = useCallback(async () => {
    try {
      const data = await fetchOrders();
      const mapped = data.map(o => ({
        dbId:          o.id,
        id:            o.reference || o.id,
        date:          new Date(o.created_at).toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' }),
        createdAt:     o.created_at,
        updatedAt:     o.updated_at,
        status:        (o.status || 'pending').toLowerCase(),
        paymentStatus: (o.payment_status || 'unpaid').toLowerCase(),
        items: (o.items || []).map(i => ({
          name:  i.product_name || i.product?.name || 'Item',
          price: i.price,
          qty:   i.quantity || 1,
          size:  i.size,
          color: i.color,
          image: i.image_url || i.product?.image_url || null,
          images: i.product?.images || null,
        })),
        total:           o.total,
        subtotal:        o.subtotal,
        shipping_fee:    o.shipping_fee,
        shippingAddress: o.delivery_address?.shipping_address || o.shipping_address || '-',
        courier_name:    o.courier_name,
        tracking_number: o.tracking_number,
        tracking_url:    o.tracking_url,
        status_history:  o.status_history,
      }));

      setOrders(mapped);
      if (selectedOrder) {
        const updated = mapped.find(o => o.id === selectedOrder.id);
        if (updated) {
          // Only sync from refresh if the refreshed data is not older than what we already have
          const refreshedAt = new Date(updated.updatedAt).getTime();
          const currentAt = new Date(selectedOrder.updatedAt).getTime();
          if (refreshedAt >= currentAt) {
            setSelectedOrder(updated);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch orders', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { 
    refreshOrders(); 
    const interval = setInterval(refreshOrders, 8000);
    return () => clearInterval(interval);
  }, [refreshOrders]);

  const executeConfirmDelivery = async () => {
    setIsConfirming(true);
    setShowConfirmModal(false);
    try {
      const res = await confirmDelivery(selectedOrder.dbId);
      await refreshOrders();
      // Immediate UI update
      setSelectedOrder(prev => ({
        ...prev,
        status: 'delivered',
        status_history: res.order?.status_history || prev.status_history
      }));
      setShowRatingModal(true);
    } catch (e) {
      console.error('Failed to confirm delivery', e);
      alert('Failed to confirm delivery. Please try again.');
    } finally {
      setIsConfirming(false);
    }
  };

  const executeCancelOrder = async () => {
    setIsProcessingAction(true);
    try {
      const res = await cancelOrder(selectedOrder.dbId);

      // Apply the server's response immediately so refund status shows right away
      if (res.order) {
        const newPaymentStatus = (res.order.payment_status || selectedOrder.paymentStatus).toLowerCase();
        const newStatus = (res.order.status || 'cancelled').toLowerCase();
        setSelectedOrder(prev => ({
          ...prev,
          status: newStatus,
          paymentStatus: newPaymentStatus,
          status_history: res.order.status_history || prev.status_history,
        }));
      }

      setShowCancelModal(false);
      toast.success('Order cancelled successfully.');
      if (res?.refund_message) {
        setTimeout(() => toast.info(res.refund_message, { autoClose: 8000 }), 600);
      }

      // Refresh in background to sync the list — but don't let it override selectedOrder
      // since we already have fresh data from the cancel response
      refreshOrders();
    } catch (e) {
      console.error('Failed to cancel order', e);
      toast.error(e.response?.data?.message || 'Failed to cancel order. Please try again.');
    } finally {
      setIsProcessingAction(false);
    }
  };

  const handlePayNow = async () => {
    if (!paystackKey) {
      toast.error('Payment gateway not configured.');
      return;
    }
    setIsProcessingAction(true);
    try {
      // Step 1: Auto-verify in case they already successfully paid but closed the window previously
      try {
        const verifyRes = await verifyOrderPayment(selectedOrder.dbId);
        if (verifyRes.status === 'paid') {
          toast.success(verifyRes.message || 'Payment was already successful!');
          
          // Update local state immediately
          setSelectedOrder(prev => ({ ...prev, paymentStatus: 'paid', status: 'processing' }));
          refreshOrders();
          
          setIsProcessingAction(false);
          return; // Stop here, no need to open payment window
        }
      } catch (e) {
        // Ignore verify errors and proceed to let them pay
      }

      // Step 2: Initialize new payment
      const initData = await initializePayment(selectedOrder.dbId);
      const amountKobo = initData.amountKobo || Math.round(selectedOrder.total * 100);

      const handler = window.PaystackPop.setup({
        key: paystackKey,
        email: selectedOrder.customer_email || 'customer@example.com',
        amount: amountKobo,
        access_code: initData.accessCode,
        onClose: () => { 
          toast.info('Payment window closed. Your order is saved — you can pay later from Account.');
          setIsProcessingAction(false);
          refreshOrders();
        },
        callback: (response) => {
          verifyPayment(response.reference)
            .then(() => { 
              setIsProcessingAction(false); 
              toast.success('Payment successful!');
              refreshOrders(); 
            })
            .catch(() => { 
              toast.error('Payment verification failed.'); 
              setIsProcessingAction(false); 
            });
        },
      });
      handler.openIframe();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Could not initialize payment.');
      setIsProcessingAction(false);
    }
  };

  /* ── DETAIL VIEW ─────────────────────────────────────────────────────────── */
  if (selectedOrder) {
    const statusInfo = getStatusInfo(selectedOrder.status, selectedOrder.paymentStatus);
    const { steps: trackingSteps, currentStepIdx } = getTrackingConfig(selectedOrder);
    const isCancelled   = ['cancelled', 'failed'].includes(selectedOrder.status);
    const canConfirm    = selectedOrder.status === 'shipped';

    return (
      <div className="min-h-screen bg-[#FAF3F0] pb-28 lg:pb-12">
        <div className="max-w-5xl mx-auto px-4 lg:px-12 xl:px-16 pt-6">

          {/* Back button */}
          <button
            onClick={() => setSelectedOrder(null)}
            className="w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-50 hover:text-[#F47B20] transition-all shadow-sm mb-5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">

            {/* Order header */}
            <div className="px-6 py-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Order Reference</p>
                <h2 className="text-lg font-black text-[#1A1A2E]">{selectedOrder.id}</h2>
                <p className="text-xs text-gray-400 mt-0.5">Placed on {selectedOrder.date}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-3 py-1.5 rounded-full text-xs font-bold border ${statusInfo.color}`}>
                  {statusInfo.label}
                </span>
                {canConfirm && (
                  <button
                    onClick={() => setShowConfirmModal(true)}
                    disabled={isConfirming}
                    className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-full text-xs font-bold transition-colors disabled:opacity-50"
                  >
                    {isConfirming ? 'Confirming…' : 'Confirm Delivery'}
                  </button>
                )}
                {selectedOrder.status === 'delivered' && (
                  <button
                    onClick={() => setShowRatingModal(true)}
                    className="px-4 py-1.5 bg-[#F47B20] hover:bg-[#D96A10] text-white rounded-full text-xs font-bold transition-colors"
                  >
                    Rate Items
                  </button>
                )}
              </div>
            </div>

            {/* ── TRACKING ── */}
            {!isCancelled && (
              <div className="px-6 py-6 border-b border-gray-100">
                <h3 className="text-sm font-bold text-[#1A1A2E] mb-5">Order Tracking</h3>

                {(selectedOrder.tracking_number || selectedOrder.courier_name) && (
                  <div className="mb-6 p-4 bg-[#F2F7F4] rounded-2xl border border-green-100 flex flex-col gap-1">
                    <p className="text-[10px] font-bold text-[#2EAD6E] uppercase tracking-wider">Shipping Details</p>
                    {selectedOrder.courier_name && <p className="text-sm font-bold text-[#1A2820]">Courier: {selectedOrder.courier_name}</p>}
                    {selectedOrder.tracking_number && <p className="text-sm font-bold text-[#1A2820]">Courier No: {selectedOrder.tracking_number}</p>}
                    {selectedOrder.tracking_url && (
                      <a href={selectedOrder.tracking_url} target="_blank" rel="noreferrer" className="text-xs font-bold text-[#F47B20] hover:underline mt-1">
                        Track Package →
                      </a>
                    )}
                  </div>
                )}

                {/* Desktop: horizontal stepper */}
                <div className="hidden sm:flex items-start relative pb-8">
                  <div className="absolute top-[22px] left-5 right-5 h-1 bg-gray-100 rounded-full z-0" />
                  <div
                    className="absolute top-[22px] left-5 h-1 bg-[#F47B20] rounded-full z-0 transition-all duration-700"
                    style={{ width: currentStepIdx === 0 ? '0%' : `${(currentStepIdx / (trackingSteps.length - 1)) * 100}%` }}
                  />
                  {trackingSteps.map((step, idx) => {
                    const done    = idx < currentStepIdx;
                    const current = idx === currentStepIdx;
                    
                    const hist = getStepHistory(selectedOrder.status_history, step.key);
                    const timeStr = hist ? new Date(hist.timestamp).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : null;

                    return (
                      <div key={step.key} className="flex-1 flex flex-col items-center relative z-10">
                        <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm transition-all shadow-sm
                          ${done    ? 'bg-[#F47B20] text-white' :
                            current ? 'bg-white border-2 border-[#F47B20] text-[#F47B20]' :
                                      'bg-white border-2 border-gray-200 text-gray-300'}`}
                        >
                          {done ? '✓' : step.icon}
                        </div>
                        <p className={`mt-3 text-center text-[11px] font-bold leading-tight
                          ${current ? 'text-[#F47B20]' : done ? 'text-gray-700' : 'text-gray-300'}`}
                        >
                          {step.label}
                        </p>
                        {timeStr && <p className="text-[9px] text-[#F47B20] font-semibold mt-1 text-center leading-tight">{timeStr}</p>}
                      </div>
                    );
                  })}
                </div>

                {/* Mobile: vertical timeline */}
                <div className="sm:hidden space-y-0">
                  {trackingSteps.map((step, idx) => {
                    const done    = idx <= currentStepIdx;
                    const current = idx === currentStepIdx;
                    const pending = idx > currentStepIdx;

                    return (
                      <div key={step.key} className="flex gap-4 relative z-10">
                        <div className="flex-shrink-0 mt-0.5">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs shadow-sm
                            ${done ? 'bg-[#F47B20] text-white' : 'bg-white border-2 border-gray-200 text-gray-300'}`}
                          >
                            {done ? '✓' : step.icon}
                          </div>
                          {idx < trackingSteps.length - 1 && (
                            <div className={`w-0.5 h-10 ml-3.5 my-1 ${done ? 'bg-[#F47B20]' : 'bg-gray-200'}`} />
                          )}
                        </div>
                        <div className="pb-6 pt-1">
                          <p className={`text-sm font-bold ${current ? 'text-[#F47B20]' : done ? 'text-gray-800' : 'text-gray-300'}`}>
                            {step.label}
                          </p>
                          {(() => {
                            const hist = getStepHistory(selectedOrder.status_history, step.key);
                            if (hist && hist.timestamp) {
                              return <p className="text-[10px] mt-0.5 text-gray-400 font-medium">{new Date(hist.timestamp).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</p>;
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── CANCELLED / FAILED STATE ── */}
            {isCancelled && (
              <div className="px-6 py-5 border-b border-gray-100 space-y-3">
                {/* Cancellation banner */}
                <div className="flex items-center gap-3 bg-red-50 border border-red-100 rounded-2xl px-4 py-3">
                  <span className="text-red-500 w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </span>
                  <div>
                    <p className="font-bold text-red-700 text-sm">Order {statusInfo.label}</p>
                    <p className="text-xs text-red-500 mt-0.5">
                      {selectedOrder.status_history?.find(h => h.status === 'cancelled')?.note || 'This order was not completed.'}
                    </p>
                  </div>
                </div>

                {/* Refund pending banner */}
                {selectedOrder.paymentStatus === 'refund_pending' && (
                  <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-2xl px-4 py-3">
                    <span className="text-orange-500 mt-0.5 flex-shrink-0">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </span>
                    <div>
                      <p className="font-bold text-orange-700 text-sm">Refund in Progress</p>
                      <p className="text-xs text-orange-600 mt-0.5">
                        A refund of <strong>{formatPrice(selectedOrder.total)}</strong> has been initiated.
                        It will reflect in your account within <strong>5–10 business days</strong> depending on your bank.
                      </p>
                    </div>
                  </div>
                )}

                {/* Refunded banner */}
                {selectedOrder.paymentStatus === 'refunded' && (
                  <div className="flex items-start gap-3 bg-teal-50 border border-teal-200 rounded-2xl px-4 py-3">
                    <span className="text-teal-600 mt-0.5 flex-shrink-0">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </span>
                    <div>
                      <p className="font-bold text-teal-700 text-sm">Refund Completed</p>
                      <p className="text-xs text-teal-600 mt-0.5">
                        <strong>{formatPrice(selectedOrder.total)}</strong> has been successfully refunded to your original payment method.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}


            {/* Items + total */}
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-sm font-bold text-[#1A1A2E] mb-3">Items Ordered</h3>
              <div className="space-y-3">
                {selectedOrder.items.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gray-50 border border-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
                      {(() => {
                        let itemMedia = null;
                        let imagesArr = [];
                        if (Array.isArray(item.images)) {
                           imagesArr = item.images;
                        } else if (typeof item.images === 'string') {
                           try { imagesArr = JSON.parse(item.images); } catch(e) {}
                        }
                        
                        if (imagesArr.length > 0) {
                           itemMedia = imagesArr.find(img => !img.match(/\.(mp4|webm|ogg)$/i));
                        }
                        if (!itemMedia && item.image && !item.image.match(/\.(mp4|webm|ogg)$/i)) {
                           itemMedia = item.image;
                        }
                        
                        if (itemMedia) {
                          return <img src={itemMedia} alt={item.name} className="w-full h-full object-cover" />;
                        }
                        return <OrderIcon className="w-5 h-5 text-gray-300" />;
                      })()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#1A1A2E] leading-tight line-clamp-2">{item.name}</p>
                      <p className="text-xs text-gray-400 truncate">Qty: {item.qty}{item.size ? ` · Size: ${item.size}` : ''}{item.color ? ` · Color: ${item.color}` : ''}</p>
                    </div>
                    <p className="text-sm font-bold text-[#F47B20]">{formatPrice(item.price * item.qty)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-gray-50 flex justify-between text-sm">
                <span className="font-bold text-gray-500">Subtotal</span>
                <span className="font-bold text-gray-800">{formatPrice(selectedOrder.subtotal || 0)}</span>
              </div>
              <div className="mt-2 flex justify-between text-sm">
                <span className="font-bold text-gray-500">Shipping</span>
                <span className="font-bold text-gray-800">{formatPrice(selectedOrder.shipping_fee || 0)}</span>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between">
                <span className="font-black text-[#1A1A2E] text-sm">Total</span>
                <span className="font-black text-[#F47B20] text-sm">{formatPrice(selectedOrder.total)}</span>
              </div>
            </div>

            {/* Delivery address + payment status */}
            <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Delivery Address</p>
                <p className="text-sm text-gray-600 leading-relaxed">{selectedOrder.shippingAddress}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Payment Status</p>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${
                  selectedOrder.paymentStatus === 'paid'           ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  selectedOrder.paymentStatus === 'refund_pending' ? 'bg-orange-50 text-orange-600 border-orange-200'   :
                  selectedOrder.paymentStatus === 'refunded'       ? 'bg-teal-50 text-teal-700 border-teal-200'         :
                  ['failed', 'cancelled'].includes(selectedOrder.paymentStatus) ? 'bg-red-50 text-red-700 border-red-200' :
                  'bg-amber-50 text-amber-700 border-amber-200'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    selectedOrder.paymentStatus === 'paid'           ? 'bg-emerald-500' :
                    selectedOrder.paymentStatus === 'refund_pending' ? 'bg-orange-400'  :
                    selectedOrder.paymentStatus === 'refunded'       ? 'bg-teal-500'    :
                    ['failed', 'cancelled'].includes(selectedOrder.paymentStatus) ? 'bg-red-500' :
                    'bg-amber-500'
                  }`} />
                  {{
                    paid:           'Payment Confirmed',
                    refund_pending: 'Refund Pending',
                    refunded:       'Refunded',
                    failed:         'Payment Failed',
                    unpaid:         'Awaiting Payment',
                  }[selectedOrder.paymentStatus] || selectedOrder.paymentStatus}
                </span>
              </div>
            </div>

            {/* Action footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 space-y-3">
              <div className="flex flex-wrap gap-3">

                {/* Pay Now — only shown when order is pending AND unpaid (not paid, not cancelled) */}
                {!isCancelled && selectedOrder.status === 'pending' && selectedOrder.paymentStatus === 'unpaid' && (
                  <button
                    onClick={handlePayNow}
                    disabled={isProcessingAction}
                    className="flex-1 text-xs font-bold text-white bg-emerald-600 py-2.5 rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50 min-w-[120px] flex items-center justify-center gap-2"
                  >
                    {isProcessingAction ? (
                      <>
                        <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Processing...
                      </>
                    ) : 'Pay Now'}
                  </button>
                )}

                {/* Cancel — shown for pending OR processing (not yet shipped) */}
                {!isCancelled && ['pending', 'processing'].includes(selectedOrder.status) && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    disabled={isProcessingAction}
                    className="flex-1 text-xs font-bold text-red-600 bg-red-50 border border-red-200 py-2.5 rounded-xl hover:bg-red-100 transition-colors disabled:opacity-50 min-w-[120px] flex items-center justify-center gap-2"
                  >
                    {isProcessingAction ? (
                      <>
                        <svg className="animate-spin h-3.5 w-3.5 text-red-500" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Cancelling...
                      </>
                    ) : 'Cancel Order'}
                  </button>
                )}
              </div>

              {/* Cancel hint — only when order is already shipped or delivered */}
              {!isCancelled && ['shipped', 'delivered'].includes(selectedOrder.status) && (
                <p className="text-[11px] text-gray-400 text-center">
                  Orders can only be cancelled before they are shipped.
                </p>
              )}
            </div>
          </div>
        </div>

        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={executeConfirmDelivery}
          title="Confirm Delivery"
          message="Are you sure you've received this order? This will mark it as delivered."
          confirmText="Yes, I Received It"
          isProcessing={isConfirming}
        />
        <ConfirmModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          onConfirm={executeCancelOrder}
          title="Cancel Order"
          message={`Are you sure you want to cancel this order?${selectedOrder.paymentStatus === 'paid' ? ` A refund of ${formatPrice(selectedOrder.total)} will be automatically initiated to your original payment method.` : ''} This action cannot be undone.`}
          confirmText="Yes, Cancel Order"
          isProcessing={isProcessingAction}
        />

        {showRatingModal && selectedOrder && (
          <RatingModal
            isOpen={showRatingModal}
            onClose={() => setShowRatingModal(false)}
            order={selectedOrder}
            onSuccess={() => {
              // Optional: You could fetch ratings to see if it's already rated
            }}
          />
        )}
      </div>
    );
  }

  /* ── LIST VIEW ────────────────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-[#FAF3F0] pb-28 lg:pb-12">
      <div className="px-4 lg:px-8 pt-6 pb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-[#1A1A2E]">My Orders</h1>
          <p className="text-gray-400 text-sm mt-0.5">Track and manage your purchases</p>
        </div>
        <button
          onClick={refreshOrders}
          className="text-xs font-bold text-[#F47B20] bg-white border border-[#F47B20]/20 px-3 py-2 rounded-xl hover:bg-[#FEF0E6] transition-colors shadow-sm"
        >
          ↻ Refresh
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-4 lg:px-12 xl:px-16">
        {loading ? (
          <div className="flex flex-col items-center py-20 gap-3">
            <div className="w-8 h-8 border-2 border-[#F47B20] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-400">Loading orders…</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center shadow-sm border border-gray-100">
            <div className="w-16 h-16 bg-[#FEF0E6] rounded-full flex items-center justify-center mx-auto mb-4">
              <OrderIcon className="w-8 h-8 text-[#F47B20]" />
            </div>
            {orders.every(o => o.paymentStatus === 'unpaid') && orders.length > 0 ? (
              <>
                <h3 className="font-bold text-[#1A1A2E] text-lg mb-2">No paid orders yet</h3>
                <p className="text-gray-500 text-sm mb-6">You have pending payments. Go to your Account page to complete them.</p>
                <button
                  onClick={() => onNavigate && onNavigate('account')}
                  className="bg-[#F47B20] hover:bg-[#D96A10] text-white font-bold py-3 px-6 rounded-xl text-sm transition-colors shadow-sm"
                >
                  Go to Account
                </button>
              </>
            ) : (
              <>
                <h3 className="font-bold text-[#1A1A2E] text-lg mb-2">No orders yet</h3>
                <p className="text-gray-500 text-sm mb-6">Start shopping and find your dream look!</p>
                <button
                  onClick={() => onNavigate && onNavigate('shop')}
                  className="bg-[#F47B20] hover:bg-[#D96A10] text-white font-bold py-3 px-6 rounded-xl text-sm transition-colors shadow-sm"
                >
                  Start Shopping
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {orders
              .filter(order => order.paymentStatus !== 'unpaid')
              .map(order => {
              const { steps: trackingSteps, currentStepIdx } = getTrackingConfig(order);
              const isCancelled   = ['cancelled', 'failed'].includes(order.status);
              return (
                <div
                  key={order.id}
                  onClick={() => setSelectedOrder(order)}
                  className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:border-[#F47B20]/30 hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex items-start gap-4">
                    {/* Product thumb */}
                    <div className="w-14 h-14 rounded-xl bg-gray-50 border border-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
                      {(() => {
                        const item = order.items[0];
                        let img = null;
                        if (item) {
                           let imagesArr = [];
                           if (Array.isArray(item.images)) {
                              imagesArr = item.images;
                           } else if (typeof item.images === 'string') {
                              try { imagesArr = JSON.parse(item.images); } catch(e) {}
                           }
                           
                           if (imagesArr.length > 0) {
                              img = imagesArr.find(i => !i.match(/\.(mp4|webm|ogg)$/i));
                           }
                           if (!img && item.image && !item.image.match(/\.(mp4|webm|ogg)$/i)) {
                              img = item.image;
                           }
                        }
                        if (img) {
                          return <img src={img} alt="Product" className="w-full h-full object-cover" />;
                        }
                        return <OrderIcon className="w-6 h-6 text-gray-300" />;
                      })()}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className="font-bold text-[#1A1A2E] text-sm">#{order.id}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${getStatusInfo(order.status, order.paymentStatus).color}`}>
                          {getStatusInfo(order.status, order.paymentStatus).label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mb-1">{order.date}</p>
                      <p className="text-xs text-gray-600 truncate">{order.items.map(i => i.name).join(', ')}</p>

                      {/* Mini progress bar */}
                      {!isCancelled && (
                        <div className="mt-2 flex items-center gap-1">
                          {trackingSteps.map((step, idx) => (
                            <div
                              key={step.key}
                              className={`h-1 rounded-full flex-1 transition-all ${
                                idx <= currentStepIdx ? 'bg-[#F47B20]' : 'bg-gray-100'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="text-right shrink-0">
                      <p className="font-black text-[#1A1A2E] text-sm">{formatPrice(order.total)}</p>
                      <p className="text-[11px] text-[#F47B20] font-bold mt-1">View</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
